import {
  OIDCClient,
  OIDCHost,
  OIDCRedirect,
  OIDCScope,
  PostLogoutRedirectURL,
} from '@/utils/env-vars';
import { decodeJWT } from '@/utils/behavior/jwt';
import { SigninSilentArgs, User, UserManager } from 'oidc-client-ts';

import { IndexedDBAuthStore } from '../indexeddb/IndexedDBAuthStore';
import { MutexLockNames } from './synchronization/lockNames';
import { retryFailedCall } from './retry-failed-call';

const contextualizedLogMessage = (message: string) => `authService. ${message}`;

// TODO remove this type declaration when we upgrade TypeScript versions
// This type declaration is to ensure that the Locks API is available in the navigator object in our current TypeScript version.
declare global {
  interface Navigator {
    locks?: {
      request: <T>(
        name: string,
        options: { mode: 'exclusive' | 'shared' },
        callback: () => Promise<T>
      ) => Promise<T>;
    };
  }
}

// DEVELOPER NOTE: This type declaration is to discourage the use of the `signinSilent` method directly from the userManager.
// It is not thread-safe and should only be used in the `authService.synchronizedSessionRefresh` function.
// Keep this type definition override up to date when updating the oidc-client-ts package.
type UserManagerOverride = Omit<UserManager, 'signinSilent'> & {
  /**
   * Executes an OAuth token refresh via the refresh token present in the UserManager storage mechanism.
   * This JSDoc is an override of the official doc due to inaccuracies in the official doc.
   *
   * Official documentation at the time of writing:
   * Returns promise to trigger a silent request (via an iframe) to the authorization endpoint.
   * The result of the promise is the authenticated `User`.
   * @deprecated Use authService.synchronizedSessionRefresh instead! DO NOT USE this anywhere outside of the authService.synchronizedSessionRefresh because it is not thread-safe!!!
   */
  signinSilent(args?: SigninSilentArgs): Promise<User | null>;
};

// Developer Note: If you're debugging some oidc-client-ts edge case or auth flow related issue, turn this on.
// Log.setLogger(console);
// Log.setLevel(Log.DEBUG);

// TODO remove the export of the userManager - ideally this UserManager would be self-contained in this module.
// The module should contain all the necessary functions to interact with the user session.
export const userManager: UserManagerOverride = new UserManager(
  // Consult the documentation here
  // https://authts.github.io/oidc-client-ts/interfaces/UserManagerSettings.html
  {
    authority: OIDCHost,
    client_id: OIDCClient,
    redirect_uri: OIDCRedirect,
    scope: OIDCScope,
    // The user session will be stored in the browser's IndexedDBStorage.
    userStore: new IndexedDBAuthStore(),
    /* IMPORTANT DEVELOPER NOTE:
  automaticSilentRenew is true by default!
  We choose to turn it off here so that we can take control of the token refresh flow ourselves, rather than relying on the lib's internal implementation.
  This provides us with options for fetch retrying and atomic token refreshing between tabs.

  We need to ensure that ONLY ONE OF mechanism does the token refreshing.
  There are two available flows that rely on the lib: "automatic silent renew" "accessTokenExpiring".
  If both mechanisms do it, we can't properly synchronize them. This will then lead to race conditions and unexpected logouts.

  DO NOT change this without consulting a lead engineer.
  */
    automaticSilentRenew: false,
    // The number of seconds before the access token expiring callback from the userManager is called.
    accessTokenExpiringNotificationTimeInSeconds: 60,
    // This timeout controls the token refresh request timeout.
    silentRequestTimeoutInSeconds: 60,
  }
);

// TODO remove this before going to prod, or use a isProduction flag to inject this only on dev
// @ts-ignore
window.userManager = userManager;

// TODO document return types

export const getUser = () => userManager.getUser();

export const loginByRedirect = async () => {
  // TODO save existing route or query parameters, so that we can redirect the user back to where they were

  return userManager.signinRedirect();
};

export const loginRedirectCallback = () => userManager.signinRedirectCallback();

// TODO Do this and add it to the JSDoc below: In case any non-transient error occurs during the token refresh, the error will be allowed to bubble up, so that the consumer of this function can handle it.
/**
 * This function synchronizes the token refresh to prevent race conditions inside the same tab and across multiple tabs.
 * The function will return the refreshed user ONLY if the token refresh succeeds.
 *
 * Consumer note: You may optionally allow the user to stay in the app under an error, but only if your use case ALLOWS it and if the user still has a valid access token, otherwise this must produce a logout.
 *
 * @description
 * IMPORTANT DEVELOPER NOTE: This function should be the only place where userManager.signinSilent is called in THE WHOLE APP!
 * IMPORTANT DEVELOPER NOTE: The consumers of this function should only be OTHER authService functions!
 * IMPORTANT DEVELOPER NOTE: Do not export this function!
 * @returns
 */
const synchronizedSessionRefresh = async () => {
  try {
    console.debug(
      contextualizedLogMessage(
        'synchronizedSessionRefresh: Attempting to do a synchronized token refresh'
      )
    );

    // TODO revise/adjust this
    // TODO install and use the polyfill package
    if (!navigator || !navigator.locks) {
      console.error(
        contextualizedLogMessage(
          'synchronizedSessionRefresh: Locks API is not supported in this environment, despite the polyfill.'
        )
      );

      return userManager.signinSilent();
    }

    console.debug(
      contextualizedLogMessage(
        'synchronizedSessionRefresh: Locks API is supported, attempting to acquire a lock for token'
      )
    );

    const refreshResult = await navigator.locks.request(
      MutexLockNames.SYNCHRONIZED_TOKEN_REFRESH,
      {
        mode: 'exclusive',
      },
      async () => {
        console.debug(
          contextualizedLogMessage(
            'synchronizedSessionRefresh: Acquired lock for token refresh'
          )
        );

        // TODO Implement permanently retrying while we have transient errors
        // We should keep retrying so long as network errors are present.
        // Allow the error to bubble up only if non-transient (non-network errors occur).
        try {
          return await userManager.signinSilent();
        } catch (err) {
          // This allows for retries on transient errors
          // Safe to use with the lock approach
          return await retryFailedCall(() => userManager.signinSilent(), err, {
            // TODO - implement permanent retrying here, with callbacks to handle UI state
            retries: 600,
            retryDelayMs: 1000,
          });
        }
      }
    );

    console.debug(
      contextualizedLogMessage(
        'synchronizedSessionRefresh: Successfully refreshed session with locks API'
      )
    );

    return refreshResult;
  } finally {
    console.debug(
      contextualizedLogMessage(
        'synchronizedSessionRefresh: Finished attempting to refresh session with locks API'
      )
    );
  }
};

// TODO remove this before going to prod, or use a isProduction flag to inject this only on dev
// @ts-ignore
window.synchronizedSessionRefresh = synchronizedSessionRefresh;

/**
 * This function will attempt to refresh the user's session and it will not handle errors that may be thrown.
 * Please use carefully and with clear intent. Otherwise use the refreshSession function.
 *
 * @returns {Promise}
 * @throws
 */
export const unhandledRefreshSession = synchronizedSessionRefresh;

// TODO add the function call from
// layouts/SharedLayout.vue for updateDecodedToken here
// the updateDecodedToken function could be declared here and used there, though it requires access to the store
// we want to always have a freshly decoded access token in the state, so it would be good to do this, or to wrap this function in the SharedLayout
/**
 * This function will attempt to refresh the user's session.
 * If the refresh is successful, it will return the refreshed user.
 * If the refresh fails, it will log out the user and return null.
 * This function is intended to be used in the auth flow, where the session needs to be
 * refreshed without user intervention.
 * @returns {Promise<User | null>} Returns the refreshed user if the refresh was successful. Otherwise it logs out the user and returns null.
 */
export const refreshSession = async (): Promise<User | null> => {
  try {
    const user = await synchronizedSessionRefresh();

    if (!user || user.expired) {
      if (!user)
        console.error(
          contextualizedLogMessage(
            'Successfully executing synchronizedSessionRefresh produced no user. This is unexepcted. Logging out'
          )
        );
      else if (user.expired)
        console.error(
          contextualizedLogMessage(
            'Successfully executing synchronizedSessionRefresh resulted in an expired refresh token. This is unexepcted. Logging out'
          )
        );
      else
        console.error(
          contextualizedLogMessage(
            'Successfully executing synchronizedSessionRefresh produced an unexpected user state detected after refresh attempt. This is unexpected. Logging out'
          )
        );

      console.debug(
        contextualizedLogMessage(
          'refreshSession must provide a valid user to the consuming flow. Logging out the user due to this unexpected failure, as we might compromise UX otherwise'
        )
      );

      await logout();
      return null;
    }

    console.debug(contextualizedLogMessage('Access token refresh successful'));

    return user;
  } catch (ex) {
    console.error(
      contextualizedLogMessage(
        'Unrecoverable error encountered in refreshSession. Logging out'
      ),
      ex
    );

    await logout();
    return null;
  }
};

export const logout = () =>
  userManager.signoutRedirect({
    // redirect the user to this URL once logout is complete on the OIDC side
    post_logout_redirect_uri: PostLogoutRedirectURL,
  });

export const logoutRedirectCallback = () =>
  userManager.signoutRedirectCallback();

export const getAccessToken = async () => {
  const user = await userManager.getUser();

  if (!user) {
    console.debug('No user found. Returning empty access token');

    return null;
  }

  // TODO what if the token is being requested as its being renewed?
  // potential race condition that requires a lock

  return user.access_token;
};

/**
 * Returns whether the current access token marks the user's email as confirmed.
 * Returns null when there is no token or the token cannot be decoded.
 */
export const isEmailConfirmedFromToken = async (): Promise<boolean | null> => {
  const token = await getAccessToken();

  if (!token) {
    console.debug(
      contextualizedLogMessage(
        'No access token found. Cannot determine email confirmation status from token'
      )
    );
    return null;
  }

  try {
    const decodedToken = decodeJWT(token) as { ec?: boolean };
    return decodedToken.ec === true;
  } catch (error) {
    console.error(
      contextualizedLogMessage(
        'Failed to decode access token while checking email confirmation status'
      ),
      error
    );
    return null;
  }
};

/**
 * This function will be called when the access token is expiring.
 * It will attempt to refresh the access token.
 * If the refresh fails, and the available access token is expired, we have no choice but to log out the user (or show them that their session is no longer valid and needs to be refreshed "intrusively" through a redirect to login).
 * If the refresh fails but the available access token isn't expired, we can allow the user to stay logged in for the time being.
 */
async function accessTokenExpiringCallback() {
  console.debug(
    contextualizedLogMessage('Access token is expiring. Attempting to refresh')
  );

  try {
    const user = await synchronizedSessionRefresh();

    if (!user) {
      console.error(
        contextualizedLogMessage(
          'Successfully executing the synchronizedSessionRefresh produced no user. This is unexpected. Redirecting to login.'
        )
      );

      await loginByRedirect();
      return;
    }

    if (user.expired) {
      console.error(
        contextualizedLogMessage(
          'Successfully executing synchronizedSessionRefresh produced an expired access token. This is unexpected. Logging out.'
        )
      );
      await logout();
      return;
    }

    console.debug(
      contextualizedLogMessage(
        'Successfully executing synchronizedSessionRefresh produced a valid access token before expiry.'
      )
    );
  } catch (err) {
    console.error(
      contextualizedLogMessage(
        'Failed to refresh token before expiry. Checking to see if we can permit the user to stay for the time being'
      ),
      err
    );

    try {
      const user = await userManager.getUser();

      if (!user) {
        console.error(
          contextualizedLogMessage(
            'No user found after failed token refresh. Logging out.'
          )
        );
        await loginByRedirect();
        return;
      }

      if (user.expired) {
        console.error(
          contextualizedLogMessage(
            'User session is expired despite the failed token refresh. Logging out.'
          )
        );
        await logout();
        return;
      }

      console.debug(
        contextualizedLogMessage(
          'User session is still valid despite the failed token refresh. Allowing the user to stay logged in for the time being.'
        )
      );
    } catch (err) {
      console.error(
        contextualizedLogMessage(
          'Failed to check if the user can stay logged in despite the failed token refresh. Logging out'
        ),
        err
      );

      await logout();
      return;
    }
  }
}

// Log the case where silent renew fails.
async function silentRenewErrorCallback(context: unknown) {
  console.error(
    contextualizedLogMessage(
      'Logging a token refresh error from the addSilentRenewError callback. This log is purely diagnostic'
    ),
    context
  );
}

export const stopAutomaticSessionRefresh = () => {
  userManager.stopSilentRenew();
};

export const removeUserManagerCallbacks = () => {
  userManager.events.removeAccessTokenExpiring(accessTokenExpiringCallback);
  userManager.events.removeSilentRenewError(silentRenewErrorCallback);
};

userManager.events.addAccessTokenExpiring(accessTokenExpiringCallback);
userManager.events.addSilentRenewError(silentRenewErrorCallback);
